import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    const to = location.state?.from?.pathname || "/admin/events";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(location.state?.from?.pathname || "/admin/events", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Login failed. Check credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] px-4"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md bg-white rounded-2xl border border-[color:var(--line)] p-8 shadow-sm">
        <div className="mb-6 text-center">
          <p className="eyebrow mb-3">★ TIOB ADMIN</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Staff sign-in</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-2">
            Manage events and site media. Contact the office to provision an account.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2 focus:outline-none focus:border-[color:var(--blue)]"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full rounded-lg border border-[color:var(--line)] px-3 py-2 focus:outline-none focus:border-[color:var(--blue)]"
              autoComplete="current-password"
            />
          </div>

          {err && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
